import React, { useState, useMemo, useId, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Button, Container, Header, Segment, Icon, Input, Modal, List, Table, Loader, Dropdown, DropdownProps, DropdownItemProps } from 'semantic-ui-react';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { useToast } from './utils/Toast';
import DatePicker from './DatePicker';
import * as XLSX from 'xlsx';
import { isMockAuthEnabled, getMockSiteById, getMockUsers } from './utils/mockAuth';
import { handleSessionExpired } from './utils/sessionHandler';
import { createSite, fetchSiteById, SiteRecord, updateSite, AuthTokenExpiredError, AccessDeniedError } from './api/sites';
import { ClientUserSummary, fetchManagedClients } from './api/users';
import { uploadFileToStorage, uploadDataUrlToStorage, deleteAssetByPublicUrl, getStoragePathFromPublicUrl, refreshPublicUrl } from './utils/storage';
import './SiteCreation.css';

interface HazardEntry {
  id: number;
  itemNumber: number;
  areaLevel: string;
  roomLocation: string;
  buildingFeature: string;
  materialItem: string;
  hazardType: string;
  hazardStatus: string;
  sampleNumber: string;
  friability: string;
  sourceOfAsbestos: string;
  disturbancePotential: string;
  condition: string;
  riskStatus: string;
  approximateQuantity: string;
  comments: string;
  controlPriority: string;
  recommendations: string;
  reInspectionDate: string;
  photograph: string;
  photographFileName: string;
  photoStoragePath?: string | null;
  isPhotoUploading: boolean;
  photoUploadProgress: number | null;
}

type PersistedHazardEntry = Omit<HazardEntry, 'isPhotoUploading' | 'photoUploadProgress'>;

interface MapPoint {
  x: number;
  y: number;
}

interface MapRectangle {
  id: number;
  topLeft: MapPoint;
  bottomRight: MapPoint;
}

interface MapHazardPoint {
  id: number;
  position: MapPoint;
  hazardEntryId: number;
  rectangleId: number;
}

type MapTool = 'none' | 'draw' | 'point';

const describeHazardOption = (entry: HazardEntry) => {
  const locationParts = [entry.areaLevel, entry.roomLocation].filter(Boolean);
  const locationLabel = locationParts.join(' · ');
  const descriptor = entry.materialItem || entry.buildingFeature || entry.hazardType || entry.sourceOfAsbestos || entry.comments;
  const primary = locationLabel || descriptor || 'Hazard item';
  const secondaryParts: string[] = [];

  if (locationLabel && descriptor && descriptor !== locationLabel) {
    secondaryParts.push(descriptor);
  }

  if (entry.hazardStatus) {
    secondaryParts.push(entry.hazardStatus);
  }

  const detail = secondaryParts.filter(Boolean).join(' • ');

  return {
    label: `${entry.itemNumber} - ${primary}`,
    detail
  };
};

const normalizeHeaderKey = (header: string) =>
  header
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const HEADER_FIELD_MAP: Record<string, keyof HazardEntry> = {
  item: 'itemNumber',
  itemnumber: 'itemNumber',
  itemno: 'itemNumber',
  arealevel: 'areaLevel',
  area: 'areaLevel',
  floorlevel: 'areaLevel',
  roomlocation: 'roomLocation',
  roomandlocation: 'roomLocation',
  room: 'roomLocation',
  feature: 'buildingFeature',
  buildingfeature: 'buildingFeature',
  itemdescription: 'materialItem',
  itemdesc: 'materialItem',
  materialitem: 'materialItem',
  hazardtype: 'hazardType',
  type: 'hazardType',
  hazardstatus: 'hazardStatus',
  status: 'hazardStatus',
  samplenumber: 'sampleNumber',
  sample: 'sampleNumber',
  sampleno: 'sampleNumber',
  friability: 'friability',
  sourceofasbestosthatisnotfixedorinstalled: 'sourceOfAsbestos',
  sourceofasbestos: 'sourceOfAsbestos',
  source: 'sourceOfAsbestos',
  disturbancepotential: 'disturbancePotential',
  disturbpotential: 'disturbancePotential',
  distributionpotential: 'disturbancePotential',
  condition: 'condition',
  riskstatus: 'riskStatus',
  risk: 'riskStatus',
  approximatequantity: 'approximateQuantity',
  approxquantity: 'approximateQuantity',
  quantity: 'approximateQuantity',
  comments: 'comments',
  comment: 'comments',
  controlpriority: 'controlPriority',
  priority: 'controlPriority',
  recommendations: 'recommendations',
  recommendation: 'recommendations',
  reinspectdate: 'reInspectionDate',
  reinspectiondate: 'reInspectionDate',
  reinspectedate: 'reInspectionDate',
  reinspection: 'reInspectionDate',
  photograph: 'photograph',
  photo: 'photograph',
  imagename: 'photograph',
  image: 'photograph'
};

const normalizeDateString = (value: string): string => {
  if (!value) {
    return '';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const lowered = trimmed.toLowerCase();
  if (SPECIAL_NA_MARKERS.has(lowered)) {
    return '';
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  // Handle Excel-style serialized dates (numbers)
  const numericCandidate = Number(trimmed);
  if (!Number.isNaN(numericCandidate) && numericCandidate > 59) {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    const date = new Date(excelEpoch.getTime() + numericCandidate * 86400000);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().slice(0, 10);
    }
  }

  // Handle formats like 7/22/2030 or 07/22/2030
  const mdyyyyMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (mdyyyyMatch) {
    const month = parseInt(mdyyyyMatch[1], 10);
    const day = parseInt(mdyyyyMatch[2], 10);
    let year = parseInt(mdyyyyMatch[3], 10);
    if (year < 100) {
      year += year >= 50 ? 1900 : 2000;
    }
    const candidate = new Date(Date.UTC(year, month - 1, day));
    if (!Number.isNaN(candidate.getTime())) {
      return candidate.toISOString().slice(0, 10);
    }
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    const tzAdjusted = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
    return tzAdjusted.toISOString().slice(0, 10);
  }

  return '';
};

const isDataUrl = (value: string): boolean => value.startsWith('data:');

const isLikelyBase64Content = (value: string): boolean =>
  /^[A-Za-z0-9+/=\s]+$/.test(value) && value.replace(/\s+/g, '').length > 100;

const buildDataUrlFromBase64 = (base64: string, mimeType = 'image/jpeg'): string => {
  const sanitized = base64.replace(/\s+/g, '');
  return `data:${mimeType};base64,${sanitized}`;
};

const stringFromCell = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith("'")) {
      return trimmed.slice(1).trim();
    }
    return trimmed;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }

  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  return String(value).trim();
};

const extractFileName = (value: string): string => {
  if (!value) {
    return '';
  }

  const parts = value.split(/[\\/]/);
  return parts[parts.length - 1] || '';
};

const derivePhotoFileName = (value: string): string => {
  if (!value) {
    return '';
  }

  if (value.startsWith('data:image/')) {
    const match = value.match(/^data:image\/(\w+);/i);
    const extension = match ? match[1] : 'png';
    return `imported-image.${extension}`;
  }

  const extracted = extractFileName(value);
  if (extracted) {
    const searchParamsIndex = extracted.indexOf('?');
    if (searchParamsIndex > -1) {
      return extracted.slice(0, searchParamsIndex);
    }
    return extracted;
  }

  return 'imported-image';
};

const SPECIAL_NA_MARKERS = new Set(['_', 'n/a', 'na', 'not applicable']);

const sanitizeTextValue = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (SPECIAL_NA_MARKERS.has(trimmed.toLowerCase())) {
    return '';
  }

  return trimmed;
};

const mapHazardTypeValue = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return 'na';
  }

  if (SPECIAL_NA_MARKERS.has(normalized)) {
    return 'na';
  }

  if (normalized.includes('asbestos')) {
    return 'asbestos';
  }

  return 'na';
};

const mapHazardStatusValue = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  if (!normalized || SPECIAL_NA_MARKERS.has(normalized)) {
    return 'na';
  }

  if (normalized.includes('positive') && !normalized.includes('negative')) {
    return 'positive';
  }

  if (normalized.includes('negative')) {
    return 'negative';
  }

  return 'na';
};

const mapFriabilityValue = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  if (!normalized || SPECIAL_NA_MARKERS.has(normalized)) {
    return 'na';
  }

  if (normalized.includes('non') && normalized.includes('friable')) {
    return 'non-friable';
  }

  if (normalized.includes('friable')) {
    return 'friable';
  }

  return 'na';
};

const resolvePhotographFromValue = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed || SPECIAL_NA_MARKERS.has(trimmed.toLowerCase())) {
    return { photo: '', fileName: '' };
  }

  const normalizedValue = trimmed.replace(/^'+/, '');

  const formulaMatch = normalizedValue.match(/^=\s*(IMAGE|HYPERLINK)\((.*)\)$/i);
  if (formulaMatch) {
    const inner = formulaMatch[2];
    const quotedUrlMatch = inner.match(/"([^"]+)"/);
    if (quotedUrlMatch) {
      const extracted = quotedUrlMatch[1];
      if (/^https?:\/\//i.test(extracted) || extracted.startsWith('data:image/')) {
        return { photo: extracted, fileName: derivePhotoFileName(extracted) };
      }
    }
  }

  const hyperlinkParts = normalizedValue.match(/^HYPERLINK\((.*)\)$/i);
  if (hyperlinkParts) {
    const quotedUrlMatch = hyperlinkParts[1].match(/"([^"]+)"/);
    if (quotedUrlMatch) {
      const extracted = quotedUrlMatch[1];
      if (/^https?:\/\//i.test(extracted) || extracted.startsWith('data:image/')) {
        return { photo: extracted, fileName: derivePhotoFileName(extracted) };
      }
    }
  }

  if (/^https?:\/\//i.test(normalizedValue) || normalizedValue.startsWith('data:image/')) {
    return { photo: normalizedValue, fileName: derivePhotoFileName(normalizedValue) };
  }

  const base64Candidate = normalizedValue.replace(/\s+/g, '');
  if (/^[A-Za-z0-9+/=]+$/.test(base64Candidate) && base64Candidate.length > 100) {
    let mime = 'image/jpeg';
    if (base64Candidate.startsWith('iVBORw0')) {
      mime = 'image/png';
    } else if (base64Candidate.startsWith('R0lGODdh') || base64Candidate.startsWith('R0lGODlh')) {
      mime = 'image/gif';
    }
    const dataUrl = `data:${mime};base64,${base64Candidate}`;
    return {
      photo: dataUrl,
      fileName: `imported-image.${mime.split('/')[1]}`
    };
  }

  if (/\.(png|jpe?g|gif|bmp|webp)$/i.test(normalizedValue)) {
    return { photo: '', fileName: derivePhotoFileName(normalizedValue) };
  }

  console.debug('[HazardImport] Photograph value could not be resolved', { value });
  return { photo: '', fileName: '' };
};

const SiteCreation: React.FC = () => {
  const navigate = useNavigate();
  const locationState = useLocation();
  const theme = useTheme();
  const { refreshSession, role, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  
  const [siteName, setSiteName] = useState('');
  const [location, setLocation] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<ClientUserSummary[]>([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(false);
  const [userLoadError, setUserLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [hazardEntries, setHazardEntries] = useState<HazardEntry[]>([]);
  const siteNameInputRef = React.useRef<HTMLInputElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const photoInputsRef = React.useRef<Map<number, HTMLInputElement | null>>(new Map());
  const mapImageInputRef = React.useRef<HTMLInputElement>(null);
  const mapCanvasRef = React.useRef<HTMLDivElement>(null);
  const pointDragMovedRef = React.useRef(false);
  const unauthorizedRedirectRef = React.useRef(false);

  const [mapImageSrc, setMapImageSrc] = useState('');
  const [mapImageName, setMapImageName] = useState('');
  const [mapImageStoragePath, setMapImageStoragePath] = useState<string | null>(null);
  const [isMapImageUploading, setIsMapImageUploading] = useState(false);
  const [mapRectangles, setMapRectangles] = useState<MapRectangle[]>([]);
  const [mapPoints, setMapPoints] = useState<MapHazardPoint[]>([]);
  const [activeMapTool, setActiveMapTool] = useState<MapTool>('none');
  const [currentRectangle, setCurrentRectangle] = useState<{ start: MapPoint; end: MapPoint } | null>(null);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [currentStatus, setCurrentStatus] = useState<'draft' | 'available'>('draft');
  const [prefillSiteId] = useState<string | null>(() => {
    const state = locationState.state as { siteId?: string } | undefined;
    return state?.siteId ?? null;
  });
  const [isInitializing, setIsInitializing] = useState(() => Boolean(prefillSiteId));

  const loadAvailableUsers = useCallback(async () => {
    setUsersLoading(true);
    setUserLoadError(null);

    try {
      // Check if using mock authentication
      if (isMockAuthEnabled()) {
        const mockUsers = getMockUsers();
        setAvailableUsers(mockUsers);
        setUsersLoading(false);
        return;
      }

      const users = await fetchManagedClients({
        roles: ['admin', 'admin_client', 'client'],
        createdBy: null
      });
      setAvailableUsers(users);
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        handleSessionExpired(showToast);
        return;
      }

      if (error instanceof AccessDeniedError) {
        const message = 'You do not have permission to load users.';
        setUserLoadError(message);
        setAvailableUsers([]);
        showToast(message, 'error');
        return;
      }

      const message = error instanceof Error ? error.message : 'Unable to load users right now.';
      setUserLoadError(message);
      setAvailableUsers([]);
      showToast(message, 'error');
    } finally {
      setUsersLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (authLoading || unauthorizedRedirectRef.current) {
      return;
    }

    if (role && role !== 'admin') {
      unauthorizedRedirectRef.current = true;
      showToast('You do not have permission to create or edit sites.', 'error');
      navigate('/no-access', {
        replace: true,
        state: { from: locationState.pathname ?? '/sites/create' }
      });
    }
  }, [authLoading, role, showToast, navigate, locationState.pathname]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (role !== 'admin') {
      setAvailableUsers([]);
      return;
    }

    void loadAvailableUsers();
  }, [authLoading, loadAvailableUsers, role]);

  const applySiteRecord = useCallback(async (site: SiteRecord) => {
    setSiteId(site.id);
    setCurrentStatus(site.status ?? 'draft');
    setSiteName(site.siteName ?? '');
    setLocation(site.location ?? '');
  setSelectedUsers((site.selectedUsers ?? []).map((value) => String(value)));

    const normalizedHazards: HazardEntry[] = await Promise.all(
      (site.hazardEntries ?? []).map(async (entry) => {
        const storagePath = (entry as HazardEntry).photoStoragePath ?? null;
        let photographValue = entry.photograph ?? '';

        if (!photographValue && storagePath) {
          try {
            photographValue = await refreshPublicUrl(storagePath);
          } catch (error) {
            console.warn('Failed to refresh photograph URL from storage path', { storagePath, error });
          }
        }

        return {
          ...entry,
          photograph: photographValue,
          photoStoragePath: storagePath,
          isPhotoUploading: false,
          photoUploadProgress: null
        } as HazardEntry;
      })
    );

    setHazardEntries(normalizedHazards);

    const mapSrc = site.mapImageSrc ?? '';
    setMapImageSrc(mapSrc);
    setMapImageName(site.mapImageName ?? '');
    setMapImageStoragePath(mapSrc ? getStoragePathFromPublicUrl(mapSrc) : null);
    setIsMapImageUploading(false);
    setMapRectangles(site.mapRectangles ?? []);
    setMapPoints(site.mapPoints ?? []);
    const overlayActive = Boolean(site.mapOverlayActive);
    setMapOverlayActive(overlayActive);
    setActiveMapTool(overlayActive ? 'point' : 'none');
  }, []);

  useEffect(() => {
    let isMounted = true;

    const hydrateSite = async () => {
      if (authLoading) {
        return;
      }

      if (!prefillSiteId) {
        setIsInitializing(false);
        return;
      }

      if (role && role !== 'admin') {
        setIsInitializing(false);
        return;
      }

      setIsInitializing(true);

      try {
        // Check if using mock authentication
        let site: SiteRecord;
        if (isMockAuthEnabled()) {
          const mockSite = getMockSiteById(prefillSiteId);
          if (!mockSite || !mockSite.drafts || mockSite.drafts.length === 0) {
            if (!isMounted) return;
            showToast('Site not found.', 'error');
            navigate('/dashboard');
            return;
          }
          
          // Convert MockSite to SiteRecord format
          const draft = mockSite.drafts[0];
          site = {
            id: mockSite.id,
            ownerUserId: mockSite.createdBy,
            createdAt: mockSite.createdAt,
            updatedAt: draft.updatedAt || mockSite.createdAt,
            siteName: mockSite.name,
            location: mockSite.location,
            status: 'available' as const,
            selectedUsers: mockSite.accessibleBy || [],
            hazardEntries: draft.hazards?.map((h: any, idx: number) => ({
              id: idx + 1,
              itemNumber: idx + 1,
              areaLevel: '',
              roomLocation: h.description || '',
              buildingFeature: '',
              materialItem: h.hazardType || '',
              hazardType: h.hazardType || '',
              hazardStatus: h.hazardStatus || 'Active',
              sampleNumber: '',
              friability: '',
              sourceOfAsbestos: '',
              disturbancePotential: '',
              condition: h.riskRating || 'Good',
              riskStatus: h.riskRating || 'Low',
              approximateQuantity: '',
              comments: h.controls || '',
              controlPriority: h.likelihood || 'Low',
              recommendations: h.controls || '',
              reInspectionDate: '',
              photograph: '',
              photographFileName: ''
            })) || [],
            mapImageSrc: '',
            mapImageName: '',
            mapRectangles: draft.mapRectangles?.map((r: any, idx: number) => ({
              id: idx + 1,
              topLeft: { x: r.topLeft?.lat || 0, y: r.topLeft?.lng || 0 },
              bottomRight: { x: r.bottomRight?.lat || 0, y: r.bottomRight?.lng || 0 }
            })) || [],
            mapPoints: [],
            mapOverlayActive: false,
            notes: null
          };
        } else {
          site = await fetchSiteById(prefillSiteId);
        }

        if (!isMounted) {
          return;
        }

        await applySiteRecord(site);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        if (error instanceof AuthTokenExpiredError) {
          handleSessionExpired(showToast);
          return;
        }

        if (error instanceof AccessDeniedError) {
          showToast('You do not have permission to edit this site.', 'error');
          navigate('/no-access', {
            replace: true,
            state: { from: locationState.pathname ?? '/sites/create' }
          });
          return;
        }

        const message = error instanceof Error ? error.message : 'Unable to load site.';
        showToast(message, 'error');
      } finally {
        if (isMounted) {
          setIsInitializing(false);
        }
      }
    };

    hydrateSite();

    return () => {
      isMounted = false;
    };
  }, [prefillSiteId, authLoading, role, applySiteRecord, showToast, refreshSession, navigate, locationState.pathname]);
  const [isDrawingRectangle, setIsDrawingRectangle] = useState(false);
  const [mapOverlayActive, setMapOverlayActive] = useState(false);
  const [pointConfigOpen, setPointConfigOpen] = useState(false);
  const [pendingPoint, setPendingPoint] = useState<MapPoint | null>(null);
  const [pendingPointRectangleId, setPendingPointRectangleId] = useState<number | null>(null);
  const [selectedPointHazardId, setSelectedPointHazardId] = useState<number | null>(null);
  const [draggingPointId, setDraggingPointId] = useState<number | null>(null);
  const [editingPointId, setEditingPointId] = useState<number | null>(null);

  const logoSrc = theme === 'dark' 
    ? '/assets/Pilbara-risk-solution-logo-whitetext.svg'
    : '/assets/Pilbara-risk-solution-logo-blacktext.svg';

  const sortedUsers = useMemo(() => {
    const entries = [...availableUsers];
    return entries.sort((a, b) => {
      const nameA = (a.fullName?.trim() || a.email).toLowerCase();
      const nameB = (b.fullName?.trim() || b.email).toLowerCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
  }, [availableUsers]);

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return sortedUsers;
    }

    return sortedUsers.filter((user) => {
      const name = user.fullName?.toLowerCase() ?? '';
      return name.includes(query) || user.email.toLowerCase().includes(query);
    });
  }, [sortedUsers, searchQuery]);

  const hazardEntryMap = useMemo(() => {
    const map = new Map<number, HazardEntry>();
    hazardEntries.forEach(entry => map.set(entry.id, entry));
    return map;
  }, [hazardEntries]);

  const usedHazardIds = useMemo(() => new Set(mapPoints.map(point => point.hazardEntryId)), [mapPoints]);

  const editingPoint = useMemo(() => {
    if (editingPointId === null) {
      return null;
    }
    return mapPoints.find(point => point.id === editingPointId) ?? null;
  }, [editingPointId, mapPoints]);

  const hazardItemOptions = useMemo<DropdownItemProps[]>(() => {
    if (hazardEntries.length === 0) {
      return [];
    }

    return hazardEntries
      .slice()
      .sort((a, b) => a.itemNumber - b.itemNumber)
      .map(entry => {
        const { label, detail } = describeHazardOption(entry);
        const isUsed = usedHazardIds.has(entry.id);
        const isEditingCurrent = editingPoint?.hazardEntryId === entry.id;
        const descriptionParts: string[] = [];

        if (detail) {
          descriptionParts.push(detail);
        }

        if (isUsed && !isEditingCurrent) {
          descriptionParts.push('Already mapped');
        }

        return {
          key: entry.id,
          value: entry.id,
          text: label,
          description: descriptionParts.length > 0 ? descriptionParts.join(' • ') : undefined,
          disabled: isUsed && !isEditingCurrent,
          className: isUsed && !isEditingCurrent ? 'hazard-dropdown-item disabled-item' : 'hazard-dropdown-item'
        };
      });
  }, [hazardEntries, usedHazardIds, editingPoint]);

  const handleHazardDropdownChange = useCallback(
    (_: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
      const rawValue = data.value;
      const parsedValue =
        typeof rawValue === 'number'
          ? rawValue
          : rawValue === undefined || rawValue === null
            ? null
            : Number(rawValue);

      setSelectedPointHazardId(
        parsedValue === null || Number.isNaN(parsedValue) ? null : parsedValue
      );
    },
    []
  );

  const closePointConfiguration = useCallback(() => {
    setPointConfigOpen(false);
    setPendingPoint(null);
    setPendingPointRectangleId(null);
    setEditingPointId(null);
    pointDragMovedRef.current = false;
  }, []);

  const resetFormState = useCallback(() => {
    setSiteName('');
    setLocation('');
    setSelectedUsers([]);
    setHazardEntries([]);
    setMapImageSrc('');
    setMapImageName('');
    setMapImageStoragePath(null);
    setIsMapImageUploading(false);
    setMapRectangles([]);
    setMapPoints([]);
    setMapOverlayActive(false);
    setActiveMapTool('none');
    setCurrentRectangle(null);
    setPendingPoint(null);
    setPendingPointRectangleId(null);
    setSelectedPointHazardId(null);
    setEditingPointId(null);
    setSiteId(null);
    setCurrentStatus('draft');
    setSearchQuery('');
    setModalOpen(false);
    pointDragMovedRef.current = false;
    setIsInitializing(false);
  }, []);

  useEffect(() => {
    if (hazardEntries.length === 0) {
      closePointConfiguration();
      setMapPoints(prev => (prev.length > 0 ? [] : prev));
      setSelectedPointHazardId(null);
      return;
    }

    const validIds = new Set(hazardEntries.map(entry => entry.id));
    setMapPoints(prev => {
      const filtered = prev.filter(point => validIds.has(point.hazardEntryId));
      return filtered.length === prev.length ? prev : filtered;
    });

    setSelectedPointHazardId(prev => {
      const matchingOption = hazardItemOptions.find(option => option.value === prev && !option.disabled);
      if (matchingOption && typeof matchingOption.value === 'number') {
        return matchingOption.value;
      }

      const firstAvailable = hazardItemOptions.find(option => !option.disabled);
      if (firstAvailable && typeof firstAvailable.value === 'number') {
        return firstAvailable.value;
      }

      return null;
    });
  }, [hazardEntries, closePointConfiguration, hazardItemOptions]);

  useEffect(() => {
    if (pendingPointRectangleId === null) {
      return;
    }

    const stillExists = mapRectangles.some(rect => rect.id === pendingPointRectangleId);
    if (!stillExists) {
      closePointConfiguration();
    }
  }, [mapRectangles, pendingPointRectangleId, closePointConfiguration]);

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAll = () => {
    setSelectedUsers(filteredUsers.map(u => u.id));
  };

  const deselectAll = () => {
    setSelectedUsers([]);
  };

  const rawMapId = useId();
  const sanitizedMapId = useMemo(() => rawMapId.replace(/:/g, ''), [rawMapId]);
  const mapPatternId = `${sanitizedMapId}-pattern`;
  const mapMaskId = `${sanitizedMapId}-mask`;

  const triggerMapImagePicker = () => {
    if (isMapImageUploading) {
      return;
    }
    mapImageInputRef.current?.click();
  };

  const handleMapImageSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const previousImageUrl = mapImageSrc;
    setIsMapImageUploading(true);

    try {
      const { publicUrl, path } = await uploadFileToStorage(file, {
        folder: 'map-images',
        fileName: file.name
      });

      setMapImageSrc(publicUrl);
      setMapImageName(file.name);
      setMapImageStoragePath(path);
      setMapRectangles([]);
      setMapPoints([]);
      setCurrentRectangle(null);
      setIsDrawingRectangle(false);
      setMapOverlayActive(false);
      setActiveMapTool('none');
      setSelectedPointHazardId(null);
      setDraggingPointId(null);
      closePointConfiguration();
      showToast('Map image uploaded.', 'success');

      if (previousImageUrl && getStoragePathFromPublicUrl(previousImageUrl)) {
        try {
          await deleteAssetByPublicUrl(previousImageUrl);
        } catch (error) {
          console.warn('Failed to remove previous map image from storage', error);
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to upload map image right now.';
      showToast(message, 'error');
      console.error('Map image upload failed:', error);
    } finally {
      setIsMapImageUploading(false);
      event.target.value = '';
    }
  };

  const handleMapToolSelect = (tool: MapTool) => {
    setActiveMapTool(tool);

    if (tool === 'draw') {
      setMapOverlayActive(false);
      setMapPoints([]);
      setPendingPoint(null);
      setPendingPointRectangleId(null);
      closePointConfiguration();
    }

    if (tool !== 'draw') {
      setCurrentRectangle(null);
      setIsDrawingRectangle(false);
    }
  };

  const toRelativePoint = (
    event: React.MouseEvent<HTMLDivElement>
  ): MapPoint | null => {
    const canvas = mapCanvasRef.current;
    if (!canvas) {
      return null;
    }

    const bounds = canvas.getBoundingClientRect();
    const relativeX = ((event.clientX - bounds.left) / bounds.width) * 100;
    const relativeY = ((event.clientY - bounds.top) / bounds.height) * 100;

    if (Number.isNaN(relativeX) || Number.isNaN(relativeY)) {
      return null;
    }

    return {
      x: Math.min(100, Math.max(0, relativeX)),
      y: Math.min(100, Math.max(0, relativeY))
    };
  };

  const normalizeRectangle = (start: MapPoint, end: MapPoint) => {
    const top = Math.min(start.y, end.y);
    const left = Math.min(start.x, end.x);
    const bottom = Math.max(start.y, end.y);
    const right = Math.max(start.x, end.x);

    return {
      topLeft: { x: left, y: top },
      bottomRight: { x: right, y: bottom }
    };
  };

  const handleMapCanvasMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (activeMapTool !== 'draw' || mapOverlayActive) {
      return;
    }

    const point = toRelativePoint(event);
    if (!point) {
      return;
    }

    setCurrentRectangle({ start: point, end: point });
    setIsDrawingRectangle(true);
  };

  const handleMapCanvasMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (draggingPointId !== null) {
      const point = toRelativePoint(event);
      if (!point) {
        return;
      }

      setMapPoints(prevPoints =>
        prevPoints.map(mapPoint => {
          if (mapPoint.id !== draggingPointId) {
            return mapPoint;
          }
          const rectangle = mapRectangles.find(rect => rect.id === mapPoint.rectangleId);
          const constrainedPoint = rectangle
            ? clampPointToRectangle(point, rectangle)
            : clampPointToStage(point);
          return {
            ...mapPoint,
            position: constrainedPoint
          };
        })
      );
      pointDragMovedRef.current = true;
      return;
    }

    if (!isDrawingRectangle || !currentRectangle) {
      return;
    }

    const point = toRelativePoint(event);
    if (!point) {
      return;
    }

    setCurrentRectangle(prev => (prev ? { ...prev, end: point } : prev));
  };

  const commitRectangle = (start: MapPoint, end: MapPoint) => {
    const { topLeft, bottomRight } = normalizeRectangle(start, end);
    const width = bottomRight.x - topLeft.x;
    const height = bottomRight.y - topLeft.y;

    if (width < 1 || height < 1) {
      return;
    }

    setMapRectangles(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        topLeft,
        bottomRight
      }
    ]);
  };

  const handleMapCanvasMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    if (draggingPointId !== null) {
      setDraggingPointId(null);
      pointDragMovedRef.current = false;
      return;
    }

    if (!isDrawingRectangle || !currentRectangle) {
      return;
    }

    const point = toRelativePoint(event);
    const endPoint = point ?? currentRectangle.end;
    commitRectangle(currentRectangle.start, endPoint);
    setCurrentRectangle(null);
    setIsDrawingRectangle(false);
  };

  const handleMapCanvasMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
    if (draggingPointId !== null) {
      setDraggingPointId(null);
      pointDragMovedRef.current = false;
      return;
    }

    if (!isDrawingRectangle || !currentRectangle) {
      return;
    }

    const point = toRelativePoint(event);
    if (point) {
      commitRectangle(currentRectangle.start, point);
    }
    setCurrentRectangle(null);
    setIsDrawingRectangle(false);
  };

  const handleMapCanvasClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (pointConfigOpen) {
      return;
    }

    if (activeMapTool !== 'point' || !mapOverlayActive) {
      return;
    }

    if ((event.target as HTMLElement).closest('.map-hazard-point')) {
      return;
    }

    event.preventDefault();

    if (hazardEntries.length === 0) {
      showToast('Add hazard register entries before placing points.', 'warning');
      return;
    }

    const canvasPoint = toRelativePoint(event);
    if (!canvasPoint) {
      return;
    }

    const containingRect = mapRectangles.find(rect => isPointInsideRectangle(canvasPoint, rect));
    if (!containingRect) {
      showToast('Points must be placed inside one of your rectangles.', 'warning');
      return;
    }

    const availableHazards = hazardEntries.filter(entry => !usedHazardIds.has(entry.id));
    if (availableHazards.length === 0) {
      showToast('Every hazard item already has a map point. Remove or edit an existing point before adding another.', 'info');
      return;
    }

    const nextDefaultHazardId = availableHazards
      .slice()
      .sort((a, b) => a.itemNumber - b.itemNumber)[0]?.id ?? null;

    setPendingPoint(canvasPoint);
    setPendingPointRectangleId(containingRect.id);
    setEditingPointId(null);
    setSelectedPointHazardId(prevSelected => {
      if (
        prevSelected !== null &&
        !usedHazardIds.has(prevSelected) &&
        hazardEntries.some(entry => entry.id === prevSelected)
      ) {
        return prevSelected;
      }
      return nextDefaultHazardId;
    });
    pointDragMovedRef.current = false;
    setPointConfigOpen(true);
  };

  const confirmPointPlacement = () => {
    if (selectedPointHazardId === null) {
      showToast('Select the hazard item this point references.', 'warning');
      return;
    }

    const hazardAlreadyMapped = mapPoints.some(
      point => point.hazardEntryId === selectedPointHazardId && (editingPointId === null || point.id !== editingPointId)
    );

    if (hazardAlreadyMapped) {
      showToast('That hazard item already has a map point assigned.', 'warning');
      return;
    }

    if (editingPointId === null) {
      if (!pendingPoint || pendingPointRectangleId === null) {
        closePointConfiguration();
        return;
      }

      const rectangle = mapRectangles.find(rect => rect.id === pendingPointRectangleId);
      if (!rectangle) {
        showToast('The rectangle for this point is no longer available.', 'error');
        closePointConfiguration();
        return;
      }

      const clampedPoint = clampPointToRectangle(pendingPoint, rectangle);
      const newPoint: MapHazardPoint = {
        id: Date.now() + Math.random(),
        position: clampedPoint,
        hazardEntryId: selectedPointHazardId,
        rectangleId: rectangle.id
      };

      setMapPoints(prev => [...prev, newPoint]);
      closePointConfiguration();
      showToast('Hazard point added to the map.', 'success');
      return;
    }

    const targetPointExists = mapPoints.some(point => point.id === editingPointId);
    if (!targetPointExists) {
      closePointConfiguration();
      return;
    }

    setMapPoints(prev =>
      prev.map(point =>
        point.id === editingPointId ? { ...point, hazardEntryId: selectedPointHazardId } : point
      )
    );
    closePointConfiguration();
    showToast('Hazard point updated.', 'success');
  };

  const handlePointMouseDown = (event: React.MouseEvent<HTMLDivElement>, pointId: number) => {
    event.stopPropagation();
    event.preventDefault();
    pointDragMovedRef.current = false;
    setDraggingPointId(pointId);
  };

  const handlePointMouseUp = (event: React.MouseEvent<HTMLDivElement>, pointId: number) => {
    event.stopPropagation();
    event.preventDefault();

    if (draggingPointId !== null) {
      setDraggingPointId(null);
    }

    if (pointDragMovedRef.current) {
      pointDragMovedRef.current = false;
      return;
    }

    const point = mapPoints.find(p => p.id === pointId);
    if (!point) {
      return;
    }

    setEditingPointId(pointId);
    setSelectedPointHazardId(point.hazardEntryId);
    setPendingPoint(point.position);
    setPendingPointRectangleId(point.rectangleId);
    pointDragMovedRef.current = false;
    setPointConfigOpen(true);
  };

  const handlePointKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, pointId: number) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    const point = mapPoints.find(p => p.id === pointId);
    if (!point) {
      return;
    }

    setEditingPointId(pointId);
    setSelectedPointHazardId(point.hazardEntryId);
    setPendingPoint(point.position);
    setPendingPointRectangleId(point.rectangleId);
    pointDragMovedRef.current = false;
    setPointConfigOpen(true);
  };

  const deleteEditingPoint = () => {
    if (editingPointId === null) {
      closePointConfiguration();
      return;
    }

    setMapPoints(prev => prev.filter(point => point.id !== editingPointId));
    showToast('Hazard point removed from the map.', 'info');
    closePointConfiguration();
  };

  const undoLastAction = () => {
    if (mapOverlayActive && mapPoints.length > 0) {
      setMapPoints(prev => prev.slice(0, -1));
      return;
    }

    if (currentRectangle) {
      setCurrentRectangle(null);
      setIsDrawingRectangle(false);
      return;
    }

    if (mapRectangles.length > 0) {
      const removedRectangle = mapRectangles[mapRectangles.length - 1];
      setMapRectangles(prev => prev.slice(0, -1));
      setMapPoints(prev =>
        removedRectangle ? prev.filter(point => point.rectangleId !== removedRectangle.id) : prev
      );
    }
  };

  const clearRectangles = () => {
    setMapRectangles([]);
    setMapPoints([]);
    setCurrentRectangle(null);
    setIsDrawingRectangle(false);
    setMapOverlayActive(false);
    setActiveMapTool('none');
    setPendingPoint(null);
    setPendingPointRectangleId(null);
    setSelectedPointHazardId(null);
    setDraggingPointId(null);
    closePointConfiguration();
  };

  const activateMapOverlay = () => {
    if (mapRectangles.length === 0) {
      showToast('Draw at least one rectangle before continuing.', 'warning');
      return;
    }

    if (hazardEntries.length === 0) {
      showToast('Add hazard register entries before placing points.', 'warning');
    }

    setActiveMapTool('point');
    setCurrentRectangle(null);
    setIsDrawingRectangle(false);
    setMapOverlayActive(true);
    setPendingPoint(null);
    setPendingPointRectangleId(null);
    closePointConfiguration();

    if (hazardEntries.length > 0) {
      const defaultHazardId = hazardEntries[0]?.id ?? null;
      setSelectedPointHazardId(prev => {
        if (prev && hazardEntries.some(entry => entry.id === prev)) {
          return prev;
        }
        return defaultHazardId;
      });
    }
  };

  const resetMapOverlay = () => {
    setMapOverlayActive(false);
    setMapPoints([]);
    setActiveMapTool('none');
    setPendingPoint(null);
    setPendingPointRectangleId(null);
    setSelectedPointHazardId(null);
    setDraggingPointId(null);
    closePointConfiguration();
  };

  const getRectDimensions = (topLeft: MapPoint, bottomRight: MapPoint) => ({
    x: topLeft.x,
    y: topLeft.y,
    width: bottomRight.x - topLeft.x,
    height: bottomRight.y - topLeft.y
  });

  const isPointInsideRectangle = (point: MapPoint, rectangle: MapRectangle) =>
    point.x >= rectangle.topLeft.x &&
    point.x <= rectangle.bottomRight.x &&
    point.y >= rectangle.topLeft.y &&
    point.y <= rectangle.bottomRight.y;

  const clampPointToRectangle = (point: MapPoint, rectangle: MapRectangle) => ({
    x: Math.min(rectangle.bottomRight.x, Math.max(rectangle.topLeft.x, point.x)),
    y: Math.min(rectangle.bottomRight.y, Math.max(rectangle.topLeft.y, point.y))
  });

  const clampPointToStage = (point: MapPoint) => ({
    x: Math.min(100, Math.max(0, point.x)),
    y: Math.min(100, Math.max(0, point.y))
  });

  const addNewHazardEntry = () => {
    const newEntry: HazardEntry = {
      id: Date.now(),
      itemNumber: hazardEntries.length > 0 ? Math.max(...hazardEntries.map(e => e.itemNumber)) + 1 : 1,
      areaLevel: '',
      roomLocation: '',
      buildingFeature: '',
      materialItem: '',
      hazardType: '',
      hazardStatus: '',
      sampleNumber: '',
      friability: '',
      sourceOfAsbestos: '',
      disturbancePotential: '',
      condition: '',
      riskStatus: '',
      approximateQuantity: '',
      comments: '',
      controlPriority: '',
      recommendations: '',
      reInspectionDate: '',
      photograph: '',
      photographFileName: '',
      photoStoragePath: null,
      isPhotoUploading: false,
      photoUploadProgress: null
    };
    setHazardEntries([...hazardEntries, newEntry]);
  };

  const updateHazardEntryFields = (id: number, updates: Partial<HazardEntry>) => {
    setHazardEntries(prevEntries =>
      prevEntries.map(entry => (entry.id === id ? { ...entry, ...updates } : entry))
    );
  };

  const updateHazardEntry = <K extends keyof HazardEntry>(
    id: number,
    field: K,
    value: HazardEntry[K]
  ) => {
    updateHazardEntryFields(id, { [field]: value } as Pick<HazardEntry, K>);
  };

  const deleteHazardEntry = (id: number) => {
    const entry = hazardEntries.find(item => item.id === id);
    if (entry?.photograph) {
      deleteAssetByPublicUrl(entry.photograph).catch((error) => {
        console.warn('Failed to remove hazard photograph while deleting entry', error);
      });
    }

    setHazardEntries(hazardEntries.filter(entry => entry.id !== id));
    photoInputsRef.current.delete(id);
    showToast('Entry deleted', 'info');
  };

  const handleImportExcel = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.debug('[HazardImport] File selected', {
      name: file.name,
      type: file.type,
      size: file.size
    });

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const buffer = e.target?.result as ArrayBuffer | undefined;
        if (!buffer) {
          showToast('Unable to read the selected file', 'error');
          console.error('[HazardImport] FileReader returned empty result');
          return;
        }

        closePointConfiguration();
        setSelectedPointHazardId(null);
        setDraggingPointId(null);

        const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
        const firstSheetName = workbook.SheetNames[0];

        console.debug('[HazardImport] Workbook parsed', {
          sheetNames: workbook.SheetNames,
          activeSheet: firstSheetName
        });

        if (!firstSheetName) {
          showToast('Spreadsheet appears to be empty', 'warning');
          return;
        }

        const worksheet = workbook.Sheets[firstSheetName];
        const parseRows = (range?: number) =>
          XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, {
            defval: '',
            raw: false,
            dateNF: 'yyyy-mm-dd',
            ...(typeof range === 'number' ? { range } : {})
          });

        const analyzeHeaders = (sampleRows: Record<string, unknown>[]) => {
          const headers = sampleRows.length > 0 ? Object.keys(sampleRows[0]) : [];
          const recognized = headers.reduce((count, header) => {
            const normalized = normalizeHeaderKey(header);
            return count + (HEADER_FIELD_MAP[normalized] ? 1 : 0);
          }, 0);
          return { headers, recognized };
        };

        let headerSource: 'first' | 'second' = 'first';
        let rows = parseRows();
        let headerAnalysis = analyzeHeaders(rows);

        console.debug('[HazardImport] Initial row extraction', {
          totalRows: rows.length,
          headersCandidate: headerAnalysis.headers,
          recognizedHeaderCount: headerAnalysis.recognized,
          headerSource
        });

        if (rows.length === 0 || headerAnalysis.recognized < 3) {
          const fallbackRows = parseRows(1);
          const fallbackAnalysis = analyzeHeaders(fallbackRows);
          console.debug('[HazardImport] Evaluating fallback header row', {
            fallbackRowCount: fallbackRows.length,
            fallbackHeaders: fallbackAnalysis.headers,
            fallbackRecognizedCount: fallbackAnalysis.recognized
          });

          if (fallbackRows.length > 0 && fallbackAnalysis.recognized >= headerAnalysis.recognized) {
            rows = fallbackRows;
            headerAnalysis = fallbackAnalysis;
            headerSource = 'second';
          }
        }

        console.debug('[HazardImport] Rows prepared for mapping', {
          totalRows: rows.length,
          headersCandidate: headerAnalysis.headers,
          recognizedHeaderCount: headerAnalysis.recognized,
          headerSourceUsed: headerSource
        });

        if (rows.length === 0) {
          showToast('No data rows were found in the spreadsheet', 'warning');
          console.debug('[HazardImport] No rows available after parsing');
          return;
        }

        const existingItemNumbers = new Set(hazardEntries.map(entry => entry.itemNumber).filter(num => Number.isFinite(num)));
        let autoItemNumber = existingItemNumbers.size > 0 ? Math.max(...existingItemNumbers) : 0;
        const baseTimestamp = Date.now();
        const newEntries: HazardEntry[] = [];

        rows.forEach((row: Record<string, unknown>, index: number) => {
          const entry: HazardEntry = {
            id: baseTimestamp + index,
            itemNumber: 0,
            areaLevel: '',
            roomLocation: '',
            buildingFeature: '',
            materialItem: '',
            hazardType: '',
            hazardStatus: '',
            sampleNumber: '',
            friability: '',
            sourceOfAsbestos: '',
            disturbancePotential: '',
            condition: '',
            riskStatus: '',
            approximateQuantity: '',
            comments: '',
            controlPriority: '',
            recommendations: '',
            reInspectionDate: '',
            photograph: '',
            photographFileName: '',
            photoStoragePath: null,
            isPhotoUploading: false,
            photoUploadProgress: null
          };

          let hasMeaningfulData = false;

          Object.entries(row).forEach(([header, rawValue]) => {
            const normalizedKey = normalizeHeaderKey(header);
            const field = HEADER_FIELD_MAP[normalizedKey];

            if (!field) {
              return;
            }

            const rawStringValue = stringFromCell(rawValue);
            const sanitizedValue = sanitizeTextValue(rawStringValue);

            if (field === 'itemNumber') {
              const numericValue = Number(rawStringValue);
              if (!Number.isNaN(numericValue) && numericValue > 0) {
                entry.itemNumber = Math.floor(numericValue);
              }
              return;
            }

            if (field === 'hazardType') {
              const mappedValue = mapHazardTypeValue(rawStringValue);
              entry.hazardType = mappedValue;
              if (mappedValue) {
                hasMeaningfulData = true;
              }
              return;
            }

            if (field === 'hazardStatus') {
              const mappedValue = mapHazardStatusValue(rawStringValue);
              entry.hazardStatus = mappedValue;
              if (mappedValue) {
                hasMeaningfulData = true;
              }
              return;
            }

            if (field === 'friability') {
              const mappedValue = mapFriabilityValue(rawStringValue);
              entry.friability = mappedValue;
              if (mappedValue) {
                hasMeaningfulData = true;
              }
              return;
            }

            if (field === 'reInspectionDate') {
              const normalizedDate = normalizeDateString(rawStringValue);
              entry.reInspectionDate = normalizedDate;
              if (normalizedDate) {
                hasMeaningfulData = true;
              }
              return;
            }

            if (field === 'photograph') {
              const resolved = resolvePhotographFromValue(rawStringValue);
              entry.photograph = resolved.photo;
              if (resolved.fileName) {
                entry.photographFileName = resolved.fileName;
              }
              if (resolved.photo) {
                hasMeaningfulData = true;
              }
              return;
            }

            if (field === 'photographFileName') {
              if (!entry.photographFileName && sanitizedValue) {
                entry.photographFileName = sanitizedValue;
              }
              if (sanitizedValue) {
                hasMeaningfulData = true;
              }
              return;
            }

            if (field in entry) {
              const assignable = sanitizedValue;
              (entry as unknown as Record<string, string | number>)[field as string] = assignable;
              if (assignable) {
                hasMeaningfulData = true;
              }
            }
          });

          if (!hasMeaningfulData) {
            console.debug('[HazardImport] Skipping row with no mapped values', { index, row });
            return;
          }

          if (!entry.itemNumber || existingItemNumbers.has(entry.itemNumber)) {
            do {
              autoItemNumber += 1;
            } while (existingItemNumbers.has(autoItemNumber));
            entry.itemNumber = autoItemNumber;
          }

          existingItemNumbers.add(entry.itemNumber);

          if (entry.photograph && !entry.photographFileName) {
            entry.photographFileName = extractFileName(entry.photograph);
          }

          newEntries.push(entry);
        });

        if (newEntries.length === 0) {
          showToast('No valid hazard entries detected in the spreadsheet', 'warning');
          console.debug('[HazardImport] No valid entries created', {
            totalRows: rows.length,
            existingItems: Array.from(existingItemNumbers)
          });
          return;
        }

        setHazardEntries(prev => [...prev, ...newEntries]);
        showToast(`Successfully imported ${newEntries.length} entr${newEntries.length === 1 ? 'y' : 'ies'}`, 'success');
        console.debug('[HazardImport] Import successful', {
          importedCount: newEntries.length,
          totalRows: rows.length
        });
      } catch (error) {
        console.error('Import error:', error);
        showToast('Error importing spreadsheet. Please confirm the template headers.', 'error');
      } finally {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    reader.onerror = () => {
      showToast('Unable to read the selected file', 'error');
      console.error('[HazardImport] FileReader failed while reading spreadsheet', reader.error);
    };

    reader.readAsArrayBuffer(file);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const registerPhotoInput = (id: number, element: HTMLInputElement | null) => {
    if (element) {
      photoInputsRef.current.set(id, element);
    } else {
      photoInputsRef.current.delete(id);
    }
  };

  const triggerPhotoPicker = (id: number) => {
    const entry = hazardEntries.find(item => item.id === id);
    if (entry?.isPhotoUploading) {
      return;
    }
    const input = photoInputsRef.current.get(id);
    input?.click();
  };

  const handlePhotographUpload = async (id: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    updateHazardEntryFields(id, { isPhotoUploading: true, photoUploadProgress: null });

    try {
      const { publicUrl, path } = await uploadFileToStorage(file, {
        folder: 'hazard-photos',
        fileName: file.name
      });

      updateHazardEntryFields(id, {
        photograph: publicUrl,
        photographFileName: file.name,
        photoStoragePath: path,
        isPhotoUploading: false,
        photoUploadProgress: null
      });

      showToast('Photograph uploaded to storage.', 'success');
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to upload photograph right now.';

      updateHazardEntryFields(id, { isPhotoUploading: false, photoUploadProgress: null });
      console.error('Photograph upload failed:', error);
      showToast(message, 'error');
    } finally {
      event.target.value = '';
    }
  };

  const previewPhotograph = (photoValue: string) => {
    if (!photoValue) {
      return;
    }

    if (/^https?:\/\//i.test(photoValue) || photoValue.startsWith('data:')) {
      const previewWindow = window.open('', '_blank', 'noopener,noreferrer');
      if (!previewWindow) {
        showToast('Enable pop-ups to preview photographs', 'warning');
        return;
      }
      previewWindow.document.write(`<!DOCTYPE html><title>Photograph Preview</title><style>body{margin:0;background:#111;display:flex;align-items:center;justify-content:center;}img{max-width:100vw;max-height:100vh;object-fit:contain;}</style><img src="${photoValue}" alt="Hazard Photograph" />`);
      previewWindow.document.close();
    } else {
      showToast('Photograph must be a valid URL or uploaded image', 'warning');
    }
  };

  const clearPhotograph = async (id: number) => {
    const entry = hazardEntries.find(item => item.id === id);
    if (!entry) {
      return;
    }

    if (entry.photograph) {
      try {
        await deleteAssetByPublicUrl(entry.photograph);
      } catch (error) {
        console.warn('Failed to remove stored hazard photograph', error);
      }
    }

    updateHazardEntryFields(id, {
      photograph: '',
      photographFileName: '',
      photoStoragePath: null,
      photoUploadProgress: null,
      isPhotoUploading: false
    });
    showToast('Photograph removed from entry', 'info');
  };

  const ensureHazardPhotosStored = useCallback(async (): Promise<HazardEntry[]> => {
    if (hazardEntries.length === 0) {
      return hazardEntries;
    }

    const updatedEntries: HazardEntry[] = [];
    let hasChanges = false;

    for (const entry of hazardEntries) {
      let nextEntry = { ...entry };

      if (!nextEntry.photograph) {
        updatedEntries.push(nextEntry);
        continue;
      }

      if (isDataUrl(nextEntry.photograph)) {
        try {
          const { publicUrl, path } = await uploadDataUrlToStorage(nextEntry.photograph, {
            folder: 'hazard-photos',
            fileName: nextEntry.photographFileName || `hazard-${nextEntry.id}`
          });
          nextEntry.photograph = publicUrl;
          nextEntry.photoStoragePath = path;
          hasChanges = true;
        } catch (error) {
          console.error('Hazard photo upload failed for data URL', error);
          throw new Error(
            `Unable to upload photograph for hazard item ${nextEntry.itemNumber}.`
          );
        }
      } else if (!/^https?:\/\//i.test(nextEntry.photograph) && isLikelyBase64Content(nextEntry.photograph)) {
        try {
          const dataUrl = buildDataUrlFromBase64(nextEntry.photograph);
          const { publicUrl, path } = await uploadDataUrlToStorage(dataUrl, {
            folder: 'hazard-photos',
            fileName: nextEntry.photographFileName || `hazard-${nextEntry.id}`
          });
          nextEntry.photograph = publicUrl;
          nextEntry.photoStoragePath = path;
          hasChanges = true;
        } catch (error) {
          console.error('Hazard photo upload failed for base64 content', error);
          throw new Error(
            `Unable to upload photograph for hazard item ${nextEntry.itemNumber}.`
          );
        }
      } else if (!nextEntry.photograph && nextEntry.photoStoragePath) {
        try {
          nextEntry.photograph = await refreshPublicUrl(nextEntry.photoStoragePath);
          hasChanges = true;
        } catch (error) {
          console.warn('Unable to refresh photograph URL for stored path', error);
        }
      }

      updatedEntries.push(nextEntry);
    }

    if (hasChanges) {
      setHazardEntries(updatedEntries);
      return updatedEntries;
    }

    return hazardEntries;
  }, [hazardEntries]);

  const ensureMapImageStored = useCallback(async () => {
    if (!mapImageSrc) {
      return { src: '', storagePath: null as string | null };
    }

    if (isDataUrl(mapImageSrc)) {
      const fileName = mapImageName || 'site-map-image';
      const { publicUrl, path } = await uploadDataUrlToStorage(mapImageSrc, {
        folder: 'map-images',
        fileName
      });
      setMapImageSrc(publicUrl);
      setMapImageStoragePath(path);
      return { src: publicUrl, storagePath: path };
    }

    if (!/^https?:\/\//i.test(mapImageSrc) && isLikelyBase64Content(mapImageSrc)) {
      const fileName = mapImageName || 'site-map-image';
      const dataUrl = buildDataUrlFromBase64(mapImageSrc, 'image/png');
      const { publicUrl, path } = await uploadDataUrlToStorage(dataUrl, {
        folder: 'map-images',
        fileName
      });
      setMapImageSrc(publicUrl);
      setMapImageStoragePath(path);
      return { src: publicUrl, storagePath: path };
    }

    if (mapImageStoragePath && !/^https?:\/\//i.test(mapImageSrc)) {
      try {
        const refreshedUrl = await refreshPublicUrl(mapImageStoragePath);
        setMapImageSrc(refreshedUrl);
        return { src: refreshedUrl, storagePath: mapImageStoragePath };
      } catch (error) {
        console.warn('Unable to refresh map image URL from storage path', error);
      }
    }

    const derivedPath = mapImageStoragePath ?? getStoragePathFromPublicUrl(mapImageSrc) ?? null;
    if (derivedPath !== mapImageStoragePath) {
      setMapImageStoragePath(derivedPath);
    }

    return { src: mapImageSrc, storagePath: derivedPath };
  }, [mapImageSrc, mapImageName, mapImageStoragePath]);

  const persistSite = useCallback(
    async (targetStatus: 'draft' | 'available') => {
      if (isInitializing || (targetStatus === 'draft' ? isSavingDraft : loading)) {
        return;
      }

      if (authLoading) {
        return;
      }

      if (role && role !== 'admin') {
        showToast('You do not have permission to perform this action.', 'error');
        navigate('/no-access', {
          replace: true,
          state: { from: locationState.pathname ?? '/sites/create' }
        });
        return;
      }

      const trimmedSiteName = siteName.trim();
      const trimmedLocation = location.trim();

      if (!trimmedSiteName) {
        showToast('Please provide a site name before saving.', 'warning');
        siteNameInputRef.current?.focus();
        return;
      }

      if (!trimmedLocation) {
        showToast('Please provide a location before saving.', 'warning');
        return;
      }

      if (selectedUsers.length === 0) {
        showToast('Please assign at least one user.', 'warning');
        return;
      }

      if (trimmedSiteName !== siteName) {
        setSiteName(trimmedSiteName);
      }

      if (trimmedLocation !== location) {
        setLocation(trimmedLocation);
      }

      if (targetStatus === 'draft') {
        setIsSavingDraft(true);
      } else {
        setLoading(true);
      }

      try {
        const totalUsers = selectedUsers.length;
        const totalHazards = hazardEntries.length;
        const storedHazards = await ensureHazardPhotosStored();
        const hazardEntriesPayload: PersistedHazardEntry[] = storedHazards.map(
          ({ isPhotoUploading: _isPhotoUploading, photoUploadProgress: _photoUploadProgress, ...rest }) => ({
            ...rest
          })
        );

        const { src: persistedMapImageSrc } = await ensureMapImageStored();

        const basePayload = {
          siteName: trimmedSiteName,
          location: trimmedLocation,
          status: targetStatus,
          selectedUsers,
          hazardEntries: hazardEntriesPayload,
          mapImageSrc: persistedMapImageSrc,
          mapImageName,
          mapRectangles,
          mapPoints,
          mapOverlayActive,
          notes: null as string | null
        };

        // Check if using mock authentication
        if (isMockAuthEnabled()) {
          const actionLabel = siteId ? 'updated' : 'created';
          const statusLabel = targetStatus === 'draft' ? 'Draft' : 'Site';
          const summary = `${totalUsers} user${totalUsers === 1 ? '' : 's'} and ${totalHazards} hazard entr${totalHazards === 1 ? 'y' : 'ies'}`;
          showToast(`${statusLabel} ${actionLabel} successfully with ${summary}.`, 'success');
          resetFormState();
          navigate('/dashboard');
          return;
        }

        if (siteId) {
          await updateSite({
            id: siteId,
            ...basePayload
          });
        } else {
          await createSite(basePayload);
        }

        const actionLabel = siteId ? 'updated' : 'created';
        const statusLabel = targetStatus === 'draft' ? 'Draft' : 'Site';
        const summary = `${totalUsers} user${totalUsers === 1 ? '' : 's'} and ${totalHazards} hazard entr${totalHazards === 1 ? 'y' : 'ies'}`;
        showToast(`${statusLabel} ${actionLabel} successfully with ${summary}.`, 'success');

        resetFormState();
        navigate('/dashboard');
      } catch (error) {
        if (error instanceof AuthTokenExpiredError) {
          handleSessionExpired(showToast);
        } else if (error instanceof AccessDeniedError) {
          showToast('You do not have permission to perform this action.', 'error');
          navigate('/no-access', {
            replace: true,
            state: { from: locationState.pathname ?? '/sites/create' }
          });
        } else {
          const message = error instanceof Error ? error.message : 'Unable to save site right now.';
          showToast(message, 'error');
        }
      } finally {
        if (targetStatus === 'draft') {
          setIsSavingDraft(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      isInitializing,
      isSavingDraft,
      loading,
      authLoading,
      role,
      siteName,
      location,
      selectedUsers,
    ensureHazardPhotosStored,
    ensureMapImageStored,
    hazardEntries,
      mapImageName,
      mapRectangles,
      mapPoints,
      mapOverlayActive,
      siteId,
      showToast,
      resetFormState,
      navigate,
      locationState.pathname
    ]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      void persistSite('available');
    },
    [persistSite]
  );

  const handleCancel = () => {
    // Navigate based on user role
    if (role === 'admin') {
      navigate('/dashboard');
    } else if (role === 'admin_client') {
      navigate('/manager-dashboard');
    } else {
      navigate('/user-dashboard');
    }
  };

  const handleSaveDraft = useCallback(() => {
    void persistSite('draft');
  }, [persistSite]);

  const handleModalClose = () => {
    setModalOpen(false);
    setSearchQuery('');
  };

  const handleModalSave = () => {
    setModalOpen(false);
    setSearchQuery('');
    if (selectedUsers.length > 0) {
      showToast(`${selectedUsers.length} user(s) selected`, 'success');
    }
  };

  const previewRectangle = currentRectangle ? normalizeRectangle(currentRectangle.start, currentRectangle.end) : null;
  const isEditing = Boolean(siteId);
  const showDraftAction = !isInitializing && currentStatus !== 'available';
  const primaryActionLabel = isEditing ? 'Save Site' : 'Create Site';
  const blockingOverlayActive = isInitializing || loading || isSavingDraft;
  const overlayMessage = isInitializing
    ? 'Loading site...'
    : loading
      ? 'Saving site...'
      : isSavingDraft
        ? 'Saving draft...'
        : 'Processing...';

  return (
    <div className="site-creation-container">
      <div className="site-creation-header">
        <Container>
          <div className="header-content">
            <img src={logoSrc} alt="Pilbara Risk Solutions" className="creation-logo" />
            <Button 
              className="back-button" 
              onClick={handleCancel}
              icon
              labelPosition='left'
            >
              <Icon name='arrow left' />
              Back to Dashboard
            </Button>
          </div>
        </Container>
      </div>

      <Container className="site-creation-main">
        <div className="creation-card">
          <div className="creation-header-section">
            <Icon name="plus circle" size="huge" className="creation-icon" />
            <Header as="h1" className="creation-title">
              Create New Site
            </Header>
            <p className="creation-subtitle">Add a new site to your dashboard</p>
          </div>

          <Segment raised className={`creation-segment${blockingOverlayActive ? ' is-loading' : ''}`}>
            {blockingOverlayActive && (
              <div className="creation-loading-overlay" role="status" aria-live="polite">
                <Loader active inverted size="large" content={overlayMessage} />
              </div>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Field required>
                <label>Site Name</label>
                <Form.Input
                  placeholder="Enter site name"
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  icon="building"
                  iconPosition="left"
                  inputRef={siteNameInputRef}
                />
              </Form.Field>

              <Form.Field required>
                <label>Location</label>
                <Form.Input
                  placeholder="Enter site location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  icon="map marker alternate"
                  iconPosition="left"
                />
              </Form.Field>

              <Form.Field required>
                <label>Assign User Access</label>
                
                <Button
                  type="button"
                  fluid
                  onClick={() => setModalOpen(true)}
                  className="open-user-modal-btn"
                  icon
                  labelPosition="left"
                >
                  <Icon name="users" />
                  {selectedUsers.length === 0 
                    ? 'Select Users' 
                    : `${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''} Selected`
                  }
                </Button>
              </Form.Field>

              <Form.Field>
                <label>
                  Hazard Register
                  <span className="hazard-count-badge">
                    {hazardEntries.length} {hazardEntries.length === 1 ? 'entry' : 'entries'}
                  </span>
                </label>
                
                <div className="hazard-register-container">
                  <div className="hazard-register-header">
                    <div className="hazard-register-actions">
                      <Button
                        type="button"
                        primary
                        icon
                        labelPosition="left"
                        onClick={addNewHazardEntry}
                        size="small"
                      >
                        <Icon name="plus" />
                        Add Entry
                      </Button>
                      <Button
                        type="button"
                        color="blue"
                        icon
                        labelPosition="left"
                        onClick={triggerFileInput}
                        size="small"
                      >
                        <Icon name="upload" />
                        Import Spreadsheet
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls,.xlsm"
                        onChange={handleImportExcel}
                        className="import-file-input"
                      />
                    </div>
                  </div>

                  <div className="hazard-table-wrapper">
                    <Table celled structured className="hazard-register-table">
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell width={1}>Item #</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Area/Level</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Room & Location</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Feature</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Item Description</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Hazard Type</Table.HeaderCell>
                          <Table.HeaderCell width={1}>Hazard Status</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Sample #</Table.HeaderCell>
                          <Table.HeaderCell width={1}>Friability</Table.HeaderCell>
                          <Table.HeaderCell width={3}>Source (Not Fixed/Installed)</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Disturb. Potential</Table.HeaderCell>
                          <Table.HeaderCell width={1}>Condition</Table.HeaderCell>
                          <Table.HeaderCell width={1}>Risk Status</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Approx. Quantity</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Comments</Table.HeaderCell>
                          <Table.HeaderCell width={1}>Control Priority</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Recommendations</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Reinspect Date</Table.HeaderCell>
                          <Table.HeaderCell width={2}>Photograph</Table.HeaderCell>
                          <Table.HeaderCell width={1}>Actions</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>

                      <Table.Body>
                        {hazardEntries.length === 0 ? (
                          <Table.Row>
                            <Table.Cell colSpan={20} textAlign="center" className="empty-table-cell">
                              <Icon name="inbox" size="large" />
                              <p>No hazard entries yet. Click "Add Entry" to get started.</p>
                            </Table.Cell>
                          </Table.Row>
                        ) : (
                          hazardEntries.map(entry => (
                            <Table.Row key={entry.id}>
                              <Table.Cell>
                                <Input
                                  type="number"
                                  value={entry.itemNumber}
                                  onChange={(e) => updateHazardEntry(entry.id, 'itemNumber', parseInt(e.target.value) || 0)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Area/Level"
                                  value={entry.areaLevel}
                                  onChange={(e) => updateHazardEntry(entry.id, 'areaLevel', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Room & Location"
                                  value={entry.roomLocation}
                                  onChange={(e) => updateHazardEntry(entry.id, 'roomLocation', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Feature"
                                  value={entry.buildingFeature}
                                  onChange={(e) => updateHazardEntry(entry.id, 'buildingFeature', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Item Description"
                                  value={entry.materialItem}
                                  onChange={(e) => updateHazardEntry(entry.id, 'materialItem', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Hazard Type"
                                  value={entry.hazardType}
                                  onChange={(e) => updateHazardEntry(entry.id, 'hazardType', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Hazard Status"
                                  value={entry.hazardStatus}
                                  onChange={(e) => updateHazardEntry(entry.id, 'hazardStatus', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Sample #"
                                  value={entry.sampleNumber}
                                  onChange={(e) => updateHazardEntry(entry.id, 'sampleNumber', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Friability"
                                  value={entry.friability}
                                  onChange={(e) => updateHazardEntry(entry.id, 'friability', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Source of Asbestos"
                                  value={entry.sourceOfAsbestos}
                                  onChange={(e) => updateHazardEntry(entry.id, 'sourceOfAsbestos', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Disturb. Potential"
                                  value={entry.disturbancePotential}
                                  onChange={(e) => updateHazardEntry(entry.id, 'disturbancePotential', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Condition"
                                  value={entry.condition}
                                  onChange={(e) => updateHazardEntry(entry.id, 'condition', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Risk Status"
                                  value={entry.riskStatus}
                                  onChange={(e) => updateHazardEntry(entry.id, 'riskStatus', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Approx. Quantity"
                                  value={entry.approximateQuantity}
                                  onChange={(e) => updateHazardEntry(entry.id, 'approximateQuantity', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Comments"
                                  value={entry.comments}
                                  onChange={(e) => updateHazardEntry(entry.id, 'comments', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Control Priority"
                                  value={entry.controlPriority}
                                  onChange={(e) => updateHazardEntry(entry.id, 'controlPriority', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <Input
                                  placeholder="Recommendations"
                                  value={entry.recommendations}
                                  onChange={(e) => updateHazardEntry(entry.id, 'recommendations', e.target.value)}
                                  className="table-input"
                                  size="small"
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <DatePicker
                                  value={entry.reInspectionDate}
                                  onChange={(date) => updateHazardEntry(entry.id, 'reInspectionDate', date)}
                                />
                              </Table.Cell>
                              <Table.Cell>
                                <div className="photo-field">
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    className={`photo-preview${entry.photograph ? ' has-photo' : ''}${entry.isPhotoUploading ? ' uploading' : ''}`}
                                    onClick={() => triggerPhotoPicker(entry.id)}
                                    onKeyDown={(ev) => {
                                      if (ev.key === 'Enter' || ev.key === ' ') {
                                        ev.preventDefault();
                                        triggerPhotoPicker(entry.id);
                                      }
                                    }}
                                    aria-label={entry.photograph ? 'Change photograph' : 'Upload photograph'}
                                    aria-busy={entry.isPhotoUploading}
                                  >
                                    {entry.isPhotoUploading ? (
                                      <div className="photo-loading">
                                        <Loader active inline="centered" size="small" />
                                        {typeof entry.photoUploadProgress === 'number' && (
                                          <span className="photo-progress-label">
                                            {entry.photoUploadProgress}%
                                          </span>
                                        )}
                                      </div>
                                    ) : entry.photograph ? (
                                      <img
                                        src={entry.photograph}
                                        alt={`Photograph for hazard item ${entry.itemNumber}`}
                                      />
                                    ) : (
                                      <div className="photo-upload-placeholder">
                                        <Icon name="upload" />
                                        <span>Upload Photo</span>
                                      </div>
                                    )}
                                  </div>
                                  {entry.photograph && (
                                    <div className="photo-actions">
                                      <Button
                                        type="button"
                                        size="mini"
                                        icon
                                        onClick={() => previewPhotograph(entry.photograph)}
                                        title="Preview Photograph"
                                      >
                                        <Icon name="image" />
                                      </Button>
                                      <Button
                                        type="button"
                                        size="mini"
                                        icon
                                        color="red"
                                        onClick={() => clearPhotograph(entry.id)}
                                        title="Remove Photograph"
                                      >
                                        <Icon name="trash" />
                                      </Button>
                                    </div>
                                  )}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    ref={(el) => registerPhotoInput(entry.id, el)}
                                    onChange={(e) => handlePhotographUpload(entry.id, e)}
                                    className="photo-hidden-input"
                                    style={{ display: 'none' }}
                                  />
                                </div>
                              </Table.Cell>
                              <Table.Cell textAlign="center">
                                <Button
                                  icon
                                  size="mini"
                                  color="red"
                                  onClick={() => deleteHazardEntry(entry.id)}
                                  title="Delete Entry"
                                >
                                  <Icon name="trash" />
                                </Button>
                              </Table.Cell>
                            </Table.Row>
                          ))
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                </div>
              </Form.Field>

              <Form.Field>
                <label>Map Builder</label>
                <div className="map-builder-section">
                  <div className="map-builder-toolbar">
                    <Button
                      type="button"
                      icon
                      labelPosition="left"
                      onClick={triggerMapImagePicker}
                      className={`map-tool-button${isMapImageUploading ? ' is-disabled' : ''}`}
                      disabled={isMapImageUploading}
                    >
                      <Icon name="image" />
                      {isMapImageUploading ? 'Uploading…' : mapImageSrc ? 'Change Image' : 'Upload Image'}
                    </Button>
                    <Button
                      type="button"
                      icon
                      labelPosition="left"
                      className={`map-tool-button${activeMapTool === 'draw' ? ' active' : ''}`}
                      onClick={() => handleMapToolSelect(activeMapTool === 'draw' ? 'none' : 'draw')}
                      disabled={!mapImageSrc || mapOverlayActive}
                    >
                      <Icon name="pencil alternate" />
                      Draw Rectangle
                    </Button>
                    <Button
                      type="button"
                      icon
                      labelPosition="left"
                      className="map-tool-button"
                      onClick={undoLastAction}
                      disabled={!currentRectangle && mapRectangles.length === 0 && mapPoints.length === 0}
                    >
                      <Icon name="undo" />
                      Undo
                    </Button>
                    <Button
                      type="button"
                      icon
                      labelPosition="left"
                      className="map-tool-button"
                      onClick={clearRectangles}
                      disabled={mapRectangles.length === 0 && !currentRectangle && mapPoints.length === 0}
                    >
                      <Icon name="trash alternate" />
                      Clear Shapes
                    </Button>
                    <Button
                      type="button"
                      icon
                      labelPosition="left"
                      className="map-tool-button"
                      onClick={resetMapOverlay}
                      disabled={!mapOverlayActive}
                    >
                      <Icon name="refresh" />
                      Reset Overlay
                    </Button>
                    <Button
                      type="button"
                      icon
                      labelPosition="left"
                      className="map-tool-button next-action"
                      onClick={activateMapOverlay}
                      disabled={!mapImageSrc || mapRectangles.length === 0 || mapOverlayActive}
                    >
                      <Icon name="arrow right" />
                      Next
                    </Button>
                    <div className="map-tool-status" aria-live="polite" />
                    <input
                      ref={mapImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleMapImageSelected}
                      className="map-upload-input"
                    />
                  </div>

                  <div className="map-builder-stage">
                    {!mapImageSrc ? (
                      <div className="map-empty-state" role="presentation" onClick={triggerMapImagePicker}>
                        <Icon name="map outline" size="huge" />
                        <p>Upload a site plan image to begin mapping.</p>
                        <span className="map-empty-cta">Click to upload</span>
                      </div>
                    ) : (
                      <div className={`map-canvas-wrapper${activeMapTool === 'draw' ? ' draw-mode' : ''}${mapOverlayActive ? ' overlay-active' : ''}`}>
                        <div
                          ref={mapCanvasRef}
                          className="map-canvas"
                          style={{ backgroundImage: `url(${mapImageSrc})` }}
                          onMouseDown={handleMapCanvasMouseDown}
                          onMouseMove={handleMapCanvasMouseMove}
                          onMouseUp={handleMapCanvasMouseUp}
                          onMouseLeave={handleMapCanvasMouseLeave}
                          onClick={handleMapCanvasClick}
                          role="presentation"
                        >
                          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="map-overlay-svg">
                            <defs>
                              <pattern id={mapPatternId} patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                                <rect width="4" height="8" fill="rgba(0,0,0,0.35)" />
                              </pattern>
                              <mask id={mapMaskId}>
                                <rect width="100" height="100" fill="white" />
                                {mapRectangles.map(rect => {
                                  const dims = getRectDimensions(rect.topLeft, rect.bottomRight);
                                  return (
                                    <rect
                                      key={`mask-${rect.id}`}
                                      x={dims.x}
                                      y={dims.y}
                                      width={dims.width}
                                      height={dims.height}
                                      fill="black"
                                    />
                                  );
                                })}
                              </mask>
                            </defs>

                            {mapRectangles.map(rect => {
                              const dims = getRectDimensions(rect.topLeft, rect.bottomRight);
                              return (
                                <rect
                                  key={rect.id}
                                  x={dims.x}
                                  y={dims.y}
                                  width={dims.width}
                                  height={dims.height}
                                  fill="none"
                                  stroke="#d92d20"
                                  strokeWidth="1.5"
                                  vectorEffect="non-scaling-stroke"
                                />
                              );
                            })}

                            {previewRectangle && !mapOverlayActive && (() => {
                              const dims = getRectDimensions(previewRectangle.topLeft, previewRectangle.bottomRight);
                              return (
                                <rect
                                  x={dims.x}
                                  y={dims.y}
                                  width={dims.width}
                                  height={dims.height}
                                  fill="none"
                                  stroke="#d92d20"
                                  strokeWidth="1.5"
                                  vectorEffect="non-scaling-stroke"
                                />
                              );
                            })()}

                            {mapOverlayActive && (
                              <g>
                                <rect
                                  width="100"
                                  height="100"
                                  fill={`url(#${mapPatternId})`}
                                  mask={`url(#${mapMaskId})`}
                                  opacity={0.85}
                                />
                                <rect
                                  x="0"
                                  y="0"
                                  width="100"
                                  height="100"
                                  fill="none"
                                  stroke="var(--bg-surface, #111417)"
                                  strokeWidth="0.5"
                                />
                              </g>
                            )}
                          </svg>

                          {mapOverlayActive && mapPoints.map(point => {
                            const hazard = hazardEntryMap.get(point.hazardEntryId);
                            return (
                              <div
                                key={point.id}
                                className="map-hazard-point"
                                style={{
                                  left: `${point.position.x}%`,
                                  top: `${point.position.y}%`
                                }}
                                onMouseDown={(event) => handlePointMouseDown(event, point.id)}
                                onMouseUp={(event) => handlePointMouseUp(event, point.id)}
                                onKeyDown={(event) => handlePointKeyDown(event, point.id)}
                                role="button"
                                tabIndex={0}
                                aria-label={hazard ? `Hazard item ${hazard.itemNumber}` : 'Hazard point'}
                                title={hazard ? `Hazard Item ${hazard.itemNumber}` : 'Hazard point'}
                              >
                                <span className="map-hazard-point-label">
                                  {hazard ? hazard.itemNumber : '—'}
                                </span>
                              </div>
                            );
                          })}

                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Form.Field>

              <Modal
                open={pointConfigOpen}
                closeOnEscape
                closeOnDimmerClick={false}
                onClose={closePointConfiguration}
                size="tiny"
                className="point-config-modal"
              >
                <Modal.Header>
                  <Icon name="map pin" style={{ marginRight: '0.75rem' }} />
                  {editingPointId !== null ? 'Edit Hazard Point' : 'Assign Hazard Item'}
                </Modal.Header>
                <Modal.Content>
                  <p className="point-config-intro">
                    {editingPointId !== null
                      ? 'Update the hazard register item linked to this point or remove it from the map.'
                      : 'Choose the hazard register item this point should reference.'}
                  </p>
                  <Dropdown
                    selection
                    fluid
                    placeholder="Select hazard item"
                    options={hazardItemOptions}
                    value={selectedPointHazardId ?? undefined}
                    onChange={handleHazardDropdownChange}
                    disabled={hazardItemOptions.length === 0 || hazardItemOptions.every(option => option.disabled)}
                    className="point-config-dropdown"
                    search={false}
                    noResultsMessage={hazardEntries.length === 0 ? 'No hazard entries available' : 'All hazards already have map points'}
                  />
                </Modal.Content>
                <Modal.Actions className="point-config-actions">
                  {editingPointId !== null && (
                    <Button
                      type="button"
                      color="red"
                      icon
                      labelPosition="left"
                      onClick={deleteEditingPoint}
                      className="point-config-delete"
                    >
                      <Icon name="trash" />
                      Delete Point
                    </Button>
                  )}
                  <div className="point-config-actions-right">
                    <Button onClick={closePointConfiguration}>
                      Cancel
                    </Button>
                    <Button
                      primary
                      onClick={confirmPointPlacement}
                      disabled={hazardItemOptions.length === 0 || selectedPointHazardId === null}
                    >
                      Save Changes
                    </Button>
                  </div>
                </Modal.Actions>
              </Modal>

              <Modal
                open={modalOpen}
                onClose={handleModalClose}
                size="small"
                className="user-selection-modal"
              >
                <Modal.Header>
                  <Icon name="users" style={{ marginRight: '1rem' }} />
                  Select Users to Assign
                </Modal.Header>
                <Modal.Content>
                  <div className="modal-search-section">
                    <Input
                      icon="search"
                      iconPosition="left"
                      placeholder="Search users by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      fluid
                      className="modal-search-input"
                    />
                    <div className="modal-bulk-actions">
                      <Button 
                        type="button"
                        size="small" 
                        onClick={selectAll}
                        className="bulk-action-btn"
                        disabled={usersLoading || filteredUsers.length === 0}
                      >
                        Select All ({filteredUsers.length})
                      </Button>
                      <Button 
                        type="button"
                        size="small" 
                        onClick={deselectAll}
                        className="bulk-action-btn"
                        disabled={selectedUsers.length === 0}
                      >
                        Clear Selection
                      </Button>
                    </div>
                  </div>

                  <div className="user-list-container">
                    {usersLoading ? (
                      <div className="no-users-found">
                        <Loader active inline="centered" />
                        <p>Loading users...</p>
                      </div>
                    ) : userLoadError ? (
                      <div className="no-users-found">
                        <Icon name="exclamation triangle" size="large" />
                        <p>{userLoadError}</p>
                        <Button
                          type="button"
                          size="small"
                          onClick={() => { void loadAvailableUsers(); }}
                          className="bulk-action-btn"
                        >
                          Retry
                        </Button>
                      </div>
                    ) : sortedUsers.length === 0 ? (
                      <div className="no-users-found">
                        <Icon name="user outline" size="large" />
                        <p>No users available yet.</p>
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="no-users-found">
                        <Icon name="search" size="large" />
                        <p>No users found matching your search</p>
                      </div>
                    ) : (
                      <List divided selection className="user-selection-list">
                        {filteredUsers.map((user) => {
                          const isSelected = selectedUsers.includes(user.id);
                          const displayName = user.fullName?.trim() && user.fullName.trim().length > 0
                            ? user.fullName.trim()
                            : user.email;

                          return (
                            <List.Item
                              key={user.id}
                              onClick={() => toggleUserSelection(user.id)}
                              className={isSelected ? 'selected' : ''}
                            >
                              <List.Content floated="right">
                                <div className="user-list-checkbox">
                                  {isSelected ? (
                                    <Icon name="check circle" color="blue" size="large" />
                                  ) : (
                                    <Icon name="circle outline" size="large" />
                                  )}
                                </div>
                              </List.Content>
                              <Icon name="user circle" size="large" className="user-list-avatar" />
                              <List.Content>
                                <List.Header className="user-list-name">{displayName}</List.Header>
                                <List.Description className="user-list-details">
                                  <span className="user-list-email">{user.email}</span>
                                </List.Description>
                              </List.Content>
                            </List.Item>
                          );
                        })}
                      </List>
                    )}
                  </div>
                </Modal.Content>
                <Modal.Actions>
                  <Button onClick={handleModalClose}>
                    Cancel
                  </Button>
                  <Button primary onClick={handleModalSave} icon labelPosition="right">
                    Done
                    <Icon name="check" />
                  </Button>
                </Modal.Actions>
              </Modal>

              <div className="button-group">
                <Button
                  type="button"
                  size="large"
                  onClick={handleCancel}
                  className="cancel-button"
                  disabled={blockingOverlayActive}
                >
                  Cancel
                </Button>
                {showDraftAction && (
                  <Button
                    type="button"
                    size="large"
                    onClick={handleSaveDraft}
                    className="save-draft-button"
                    icon
                    labelPosition="right"
                    loading={isSavingDraft}
                    disabled={blockingOverlayActive}
                  >
                    <Icon name="save" />
                    Save Draft
                  </Button>
                )}
                <Button
                  type="submit"
                  primary
                  size="large"
                  loading={loading}
                  disabled={blockingOverlayActive}
                  className="submit-button"
                  icon
                  labelPosition='right'
                >
                  <Icon name='check' />
                  {primaryActionLabel}
                </Button>
              </div>
            </Form>
          </Segment>
        </div>
      </Container>
    </div>
  );
};

export default SiteCreation;
